import type { JSX } from 'react'
import { setItCostTag, type Freshness, type ItCostsData } from './api'
import { StaleLine } from './components/FreshnessStatus'
import { FreeeCostsView } from './components/FreeeCostsView'
import { freeeAsOf } from './freeeCosts'

// The whole dashboard for a `finance-it-costs` user: the IT Software
// Subscriptions costs and nothing else. The data comes from the IT-scoped
// query (api.ts IT_COSTS_QUERY), which the backend reads as finance_it_ro, so
// there is no other number in `data` to leak. Hiding sections is not what
// keeps this page scoped; the grants are.
//
// It reuses the Costs (freee) components, so the IT row, the recurring table
// (sort, past year, drill-down, inline edit) and the raw lines read exactly as
// they do on the full dashboard. Like DashboardView it is a pure function of
// `data` plus the write handles, so the preview harness can render it too.

export function ItCostsView({
  data,
  apiUrl,
  token,
  onSaved,
  onSessionExpired,
  initialExpandedVendors = [],
}: {
  data: ItCostsData
  apiUrl: string
  token: string
  onSaved: () => void
  onSessionExpired: (message: string) => void
  initialExpandedVendors?: string[]
}): JSX.Element {
  // The freee sync is the only source this page shows, so it is the only one
  // the stale line reports (no Data sources tab here to open). Never with sheet
  // issues: those are not in this scope.
  const freshness: Freshness[] = [{ job: 'freee', ...data.itCostsFreshness, issues: [] }]

  return (
    <>
      <StaleLine freshness={freshness} />
      <p className="scope-note">
        Your access covers <strong>IT Software Subscriptions</strong> costs only. Moving a vendor to another
        label removes it from this page.
      </p>
      <FreeeCostsView
        byLabel={data.itCostsByMonth}
        recurring={data.itRecurringCosts}
        transactions={data.itCostTransactions}
        asOf={freeeAsOf(freshness, new Date().toISOString().slice(0, 10))}
        initialExpandedVendors={initialExpandedVendors}
        saveTag={(input) => setItCostTag(apiUrl, token, input)}
        editNote="Changing the label to anything other than IT Software Subscriptions moves this vendor off your page."
        onReload={onSaved}
        onSessionExpired={onSessionExpired}
      />
    </>
  )
}
